/**
 * @fileoverview OrgResolver component that resolves the :slug URL param
 * to an organization and syncs the auth context accordingly.
 * Placed inside <Route path="/org/:slug"> to wrap the app shell.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Resolves the :slug URL parameter to an organization from the user's org list.
 * - If slug matches an org, syncs auth context and renders children.
 * - If slug doesn't match any org, redirects to / (org picker).
 * - If user needs onboarding, redirects to /onboarding.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render when resolved
 * @returns {JSX.Element} Children, a redirect, or a loading indicator
 */
function OrgResolver({ children }) {
  const { slug } = useParams();
  const { orgs, activeOrg, switchOrg, needsOnboarding, initialized } = useAuth();
  const [resolved, setResolved] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);

  useEffect(() => {
    // Wait for auth to finish initializing before making any decisions
    if (!initialized) {
      return;
    }

    if (needsOnboarding) {
      setRedirectTo('/onboarding');
      return;
    }

    // Wait for orgs to be populated
    if (!orgs || orgs.length === 0) {
      return;
    }

    const matchedOrg = orgs.find(o => o.slug === slug || String(o.id) === slug);
    if (!matchedOrg) {
      setRedirectTo('/');
      return;
    }

    // If matched org is different from active, switch to it
    if (activeOrg?.id !== matchedOrg.id) {
      switchOrg(matchedOrg.id)
        .then(() => setResolved(true))
        .catch(() => setRedirectTo('/'));
    } else {
      setResolved(true);
    }
  }, [slug, orgs, activeOrg, switchOrg, needsOnboarding, initialized]);

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!resolved) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: '#4a5568',
      }}>
        Loading...
      </div>
    );
  }

  return <>{children}</>;
}

OrgResolver.propTypes = {
  children: PropTypes.node.isRequired,
};

export default OrgResolver;
